import { h } from 'preact';
import Store from 'store/store';
import { addFooterLinkInColumn, sortFooterLinksInColumn } from 'utils/footerUtils';
import * as jsonPointer from 'utils/jsonPointer';
import { alert, confirm } from 'utils/modal';
import { canUser } from 'utils/roles';
import { joinClasses } from 'utils/utils';

import Button from 'components/button/button';
import SimpleField from 'components/editor/settings/simpleField';
import SelectableSection from 'components/editor/selectableSection';
import { Sortable, SortableItem, Handle } from 'components/sortable/sortable';
import Tiktok from 'components/bolt/icons/tiktok';
import Youtube from 'components/bolt/icons/youtube';
import Instagram from 'components/bolt/icons/instagram';
import Facebook from 'components/bolt/icons/facebook';
import Twitter from 'components/bolt/icons/twitter';
import LinkedIn from 'components/bolt/icons/linkedIn';

import MovieFooterEditor from 'components/editor/movies/movieFooter/movieFooter';

import s from 'components/editor/movies/movieFooter/movieFooterFocus.sss';

export default class FocusMovieFooterEditor extends MovieFooterEditor {
	s = s;

	constructor(props) {
		super(props);

		this.renderLink = this.renderLink.bind(this);
		this.renderFocusFooter = this.renderFocusFooter.bind(this);
		this.renderSocialIcon = this.renderSocialIcon.bind(this);

		// Link methods
		this.resetFooterLinks = this.resetFooterLinks.bind(this);
		this.deleteFooterLink = this.deleteFooterLink.bind(this);

		// Focus link methods
		this.onFooterLinksColumnSort = this.onFooterLinksColumnSort.bind(this);
	}

	getGlobalLinks() {
		const navigationData = Store.get().data?.navigation?.content;

		// If no navigation data, fall back to studio config defaults
		if (!navigationData?.footer?.links) {
			return {
				0: { id: 0, name: 'About Us', destination: 'https://www.focusfeatures.com/about', external: true, order: 0, column: 1 },
				1: { id: 1, name: 'Contact Us', destination: 'https://www.focusfeatures.com/contact', external: true, order: 1, column: 1 },
				2: { id: 2, name: 'Ad Choices', destination: 'https://www.nbcuniversal.com/privacy/cookies#accordionheader2', external: true, order: 2, column: 1 },
				3: { id: 3, name: 'Privacy Policy', destination: 'https://www.nbcuniversal.com/privacy', external: true, order: 0, column: 2 },
				4: { id: 4, name: 'Cookie Preferences', destination: '#', external: true, order: 1, column: 2 },
				5: { id: 5, name: 'Terms of Use', destination: 'https://www.nbcuniversal.com/terms', external: true, order: 2, column: 2 },
			};
		}

		const globalFooterLinksRaw = navigationData.footer.links;

		// Convert navigation footer links structure to flat object with column property
		// Global links always use their order from Navigation & Legal (not per-movie overrides)
		const globalFooterLinks = {};
		let linkId = 0;

		globalFooterLinksRaw.forEach((column, columnIndex) => {
			if (Array.isArray(column)) {
				column.forEach((link, indexInColumn) => {
					globalFooterLinks[linkId] = {
						id: linkId,
						name: link.name,
						destination: link.destination,
						external: /^https?:\/\//.test(link.destination),
						order: indexInColumn,
						column: columnIndex + 1, // Column 1 or Column 2
					};
					linkId++;
				});
			}
		});

		return globalFooterLinks;
	}

	getCustomLinks() {
		// Get custom per-movie footer links
		// Both Focus and Universal use customLinks path for per-movie custom links
		const customLinks = jsonPointer.get(Store.get().data, `${this.footerPath}/customLinks`) || {};
		return customLinks;
	}

	getAllLinks() {
		// Get global links based on studio (Focus uses getGlobalLinks, Universal uses getLinks)
		const globalLinks = this.getGlobalLinks();
		const customLinks = this.getCustomLinks();

		// Mark global links as global: true
		const markedGlobalLinks = Object.entries(globalLinks).reduce((acc, [key, link]) => {
			acc[`global_${key}`] = { ...link, global: true, id: `global_${key}` };
			return acc;
		}, {});

		// Mark custom links as global: false
		const markedCustomLinks = Object.entries(customLinks).reduce((acc, [key, link]) => {
			acc[`custom_${key}`] = { ...link, global: false, id: `custom_${key}` };
			return acc;
		}, {});

		return { ...markedGlobalLinks, ...markedCustomLinks };
	}

	// ========== FOOTER METHODS ==========

	onFooterLinksColumnSort(column, e) {
		sortFooterLinksInColumn(this.footerPath, column, e);
	}

	async resetFooterLinks(column) {
		const message = `This will remove all custom links in Column ${column}. Global footer links will remain.`;
		const ok = await confirm(message, {
			title: 'Reset to default?',
			danger: true,
			button: 'Reset to default',
		});
		if (!ok) return;

		const customLinks = JSON.parse(JSON.stringify(this.getCustomLinks()));
		const remaining = {};
		let newIndex = 0;
		Object.values(customLinks).forEach(link => {
			if (link.column !== column) {
				remaining[newIndex] = { ...link, id: newIndex };
				newIndex++;
			}
		});
		jsonPointer.set(Store.get().data, `${this.footerPath}/customLinks`, remaining);

		this.props.select('/footer');
	}

	async deleteFooterLink(linkNumber) {
		// Prevent deleting global links
		if (String(linkNumber).startsWith('global_')) {
			await alert('Global links cannot be deleted. They are managed in the Navigation & Legal section.');
			return;
		}

		const ok = await confirm('This will remove the link from the footer.', {
			title: 'Delete footer link?',
			danger: true,
			button: 'Delete',
		});
		if (!ok) return;

		const customIndex = String(linkNumber).replace('custom_', '');
		const customLinks = JSON.parse(JSON.stringify(this.getCustomLinks()));
		delete customLinks[customIndex];

		const reindexed = {};
		Object.values(customLinks).forEach((link, index) => {
			reindexed[index] = { ...link, id: index, order: index };
		});

		jsonPointer.set(Store.get().data, `${this.footerPath}/customLinks`, reindexed);

		this.props.select('/footer');
	}

	renderLink(link) {
		return (
			<SelectableSection
				section={`/footer/links/${link.id}`}
				{...this.props}
				class={s.link}
				key={link.id}
			>
				{link.name}
			</SelectableSection>
		);
	}

	renderSocialIcon(social, index) {
		const isEnabled = social && social.destination;

		// Icon components
		const iconComponents = {
			facebook: Facebook,
			instagram: Instagram,
			tiktok: Tiktok,
			twitter: Twitter,
			x: Twitter,
			youtube: Youtube,
			linkedin: LinkedIn,
		};

		// Normalize the social key to lowercase and remove spaces
		let socialKey = social?.name?.toLowerCase().replace(/\s+/g, '') || '';

		// Try exact match first
		let IconComponent = iconComponents[socialKey];

		// If not found, try partial matching
		if (!IconComponent) {
			// Check if it contains twitter or x
			if (socialKey.includes('twitter') || socialKey === 'x') {
				IconComponent = Twitter;
			} else if (socialKey.includes('facebook')) {
				IconComponent = Facebook;
			} else if (socialKey.includes('instagram')) {
				IconComponent = Instagram;
			} else if (socialKey.includes('tiktok')) {
				IconComponent = Tiktok;
			} else if (socialKey.includes('youtube')) {
				IconComponent = Youtube;
			} else if (socialKey.includes('linkedin')) {
				IconComponent = LinkedIn;
			}
		}

		if (!IconComponent) {
			console.warn(`No icon found for social: "${social?.name}" (normalized: "${socialKey}")`);
			return null;
		}

		return (
			<SelectableSection
				section={`/footer/socials/${index}`}
				{...this.props}
				class={joinClasses(s.socialIcon, !isEnabled && s.socialDisabled)}
				key={index}
			>
				<div title={`Focus Features on ${social?.name}`}>
					<IconComponent height="28" />
				</div>
			</SelectableSection>
		);
	}

	renderFocusLinks() {
		// Get all links (global + custom)
		const allLinks = this.getAllLinks();
		const links = Object.values(allLinks).sort((a, b) => (a.order || 0) - (b.order || 0));

		// Get legal text from Navigation & Legal
		const navigationData = Store.get().data?.navigation?.content;
		const legalText = navigationData?.footer?.legal || `© ${this.copyrightYear} Focus Features. A Comcast Company.`;

		// Assign columns if not set yet (migration for existing data)
		const linksWithColumns = links.map((link) => {
			const column = link.column || 1;
			return { ...link, column };
		});

		// Filter and sort links by column
		const focusLinks = [
			linksWithColumns.filter(link => link.column === 1).sort((a, b) => (a.order || 0) - (b.order || 0)),
			linksWithColumns.filter(link => link.column === 2).sort((a, b) => (a.order || 0) - (b.order || 0)),
		];
		return (
			<div class={s.linksWrapper}>
				<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', minWidth: 0 }}>
					{focusLinks.map((section, i) => (
						<div class={s.links} key={i}>
							{section.map((link) => this.renderLink(link))}
						</div>
					))}
				</div>
				<div class={s.copyright}>
					{legalText.split('\n').map((line, i) => (
						<p key={i} style={{ margin: 0 }}>{line}</p>
					))}
				</div>
			</div>
		);
	}

	renderFocusSocials() {
		// Load global social links from Navigation & Legal
		const navigationData = Store.get().data?.navigation?.content;
		const globalSocials = navigationData?.footer?.social || [];

		return (
			<SelectableSection
				section={`/footer/socials`}
				{...this.props}
				class={s.shareWrapper}
				key={'socials'}
			>
				<span>Connect with Focus Features</span>
				<div class={s.share}>
					{globalSocials.map((social, index) => this.renderSocialIcon(social, index))}
				</div>
			</SelectableSection>
		);
	}

	renderFocusFooter() {
		const { halfPage } = this.props;
		return (
			<SelectableSection {...this.props} class={joinClasses(s.wrapper, this.props.class)} section="/footer">
				<div class={joinClasses(s.content, halfPage && s.halfPage)}>
					{halfPage ? this.renderFocusSocials() : this.renderFocusLinks()}
					{halfPage ? this.renderFocusLinks() : this.renderFocusSocials()}
				</div>
			</SelectableSection>
		);
	}

	renderVisual() {
		return this.renderFocusFooter();
	}

	renderFooterLinkSettings(linkNumber) {
		const readonly = !canUser('write');

		const isGlobalLink = String(linkNumber).startsWith('global_');
		if (isGlobalLink) {
			const allLinks = this.getAllLinks();
			const currentLink = allLinks[linkNumber];
			return (
				<div class={s.itemEditor}>
					<p class={s.helperText} style="margin-bottom: 20px;">
							This is a global link managed in the Navigation & Legal section.
					</p>
					<SimpleField dark label="Text" value={currentLink?.name || ''} readonly disabled />
					<SimpleField dark label="Target" value={currentLink?.destination || ''} readonly disabled />
				</div>
			);
		}
		const customIndex = String(linkNumber).replace('custom_', '');
		const linksPath = `${this.footerPath}/customLinks/${customIndex}`;
		return (
			<div class={s.itemEditor}>
				<SimpleField dark path={`${linksPath}/name`} label="Text" disabled={readonly} />
				<SimpleField dark path={`${linksPath}/destination`} label="Link URL" placeholder="https://" disabled={readonly} />
				<div class={s.buttonRow}>
					<Button dark text danger onClick={() => this.deleteFooterLink(linkNumber)} disabled={readonly}>
							Delete link
					</Button>
				</div>
			</div>
		);
	}

	renderSocialSettings(socialIndex) {
		// For Focus Features: Social links are managed globally in Navigation & Legal
		const navigationData = Store.get().data?.navigation?.content;
		const globalSocials = navigationData?.footer?.social || [];
		const social = globalSocials[parseInt(socialIndex)];

		return (
			<div class={s.itemEditor}>
				<p class={s.helperText} style={{ marginBottom: '16px', opacity: 0.7 }}>
						Social media links are managed globally in the Navigation & Legal section.
				</p>
				<SimpleField
					dark
					label="Platform"
					value={social?.name || 'Unknown'}
					readonly
					disabled
				/>
				<SimpleField
					dark
					label="Destination"
					value={social?.destination || 'Not set'}
					readonly
					disabled
				/>
			</div>
		);
	}

	renderSocialsSettings() {
		// For Focus Features: Show global social links as read-only
		const navigationData = Store.get().data?.navigation?.content;
		const globalSocials = navigationData?.footer?.social || [];

		return (
			<div class={s.itemEditor}>
				<p class={s.helperText} style={{ marginBottom: '16px', opacity: 0.7 }}>
						Social media links are managed globally in the Navigation & Legal section. Click on a link below to view details.
				</p>
				{globalSocials.map((social, index) => {
					return (
						<div class={s.item} key={index}>
							<span>{social.name}</span>
							<button class={s.edit} onClick={() => this.props.select(`/footer/socials/${index}`)}>
									View
							</button>
						</div>
					);
				})}
			</div>
		);
	}

	renderFocusFooterEditor() {
		const allLinks = this.getAllLinks();
		const links = Object.values(allLinks).sort((a, b) => (a.order || 0) - (b.order || 0));
		const readonly = !canUser('write');

		// Separate links by column and type
		const column1GlobalLinks = links.filter(link => link.column === 1 && link.global);
		const column1CustomLinks = links.filter(link => link.column === 1 && !link.global);
		const column2GlobalLinks = links.filter(link => link.column === 2 && link.global);
		const column2CustomLinks = links.filter(link => link.column === 2 && !link.global);

		return (
			<div class={s.footer}>
				<h3>Links Column 1</h3>

				{/* Global links (read-only, managed in Navigation & Legal) */}
				{column1GlobalLinks.length > 0 && (
					<div class={s.list}>
						{column1GlobalLinks.map((link) => (
							<div class={joinClasses(s.itemWrapper, s.globalLink)} key={link.id} style={{ opacity: 0.5 }}>
								<div class={s.item}>
									<span>{link.name}</span>
								</div>
							</div>
						))}
					</div>
				)}

				{/* Custom links (editable per-movie) */}
				{column1CustomLinks.length > 0 && (
					<div style={{ marginTop: column1GlobalLinks.length > 0 ? '16px' : '0' }}>
						<p class={s.helperText} style={{ marginBottom: '8px', fontSize: '12px' }}>
							Custom links:
						</p>
						<Sortable class={s.list} onChange={(e) => this.onFooterLinksColumnSort(1, e)} data-footer-links-col1 disabled={readonly}>
							{column1CustomLinks.map((link) => (
								<SortableItem class={s.itemWrapper} key={link.id} data-link={link.id}>
									<Handle class={s.handle} />
									<div class={s.item}>
										<span>{link.name}</span>
										<button class={s.edit} onClick={() => this.props.select(`/footer/links/${link.id}`)}>
											Edit
										</button>
									</div>
								</SortableItem>
							))}
						</Sortable>
					</div>
				)}

				<div class={s.buttonRow} style={{ marginTop: '16px' }}>
					<Button dark primary icon={'plus'} onClick={() => addFooterLinkInColumn(this.footerPath, 1)} disabled={readonly}>
						Add link
					</Button>
					{column1CustomLinks.length > 0 && (
						<Button dark text danger onClick={() => this.resetFooterLinks(1)} disabled={readonly}>
							Reset links
						</Button>
					)}
				</div>

				<hr class={s.divider} />

				<h3>Links Column 2</h3>

				{/* Global links (read-only, managed in Navigation & Legal) */}
				{column2GlobalLinks.length > 0 && (
					<div class={s.list}>
						{column2GlobalLinks.map((link) => (
							<div class={joinClasses(s.itemWrapper, s.globalLink)} key={link.id} style={{ opacity: 0.5 }}>
								<div class={s.item}>
									<span>{link.name}</span>
								</div>
							</div>
						))}
					</div>
				)}

				{/* Custom links (editable per-movie) */}
				{column2CustomLinks.length > 0 && (
					<div style={{ marginTop: column2GlobalLinks.length > 0 ? '16px' : '0' }}>
						<p class={s.helperText} style={{ marginBottom: '8px', fontSize: '12px' }}>
							Custom links:
						</p>
						<Sortable class={s.list} onChange={(e) => this.onFooterLinksColumnSort(2, e)} data-footer-links-col2 disabled={readonly}>
							{column2CustomLinks.map((link) => (
								<SortableItem class={s.itemWrapper} key={link.id} data-link={link.id}>
									<Handle class={s.handle} />
									<div class={s.item}>
										<span>{link.name}</span>
										<button class={s.edit} onClick={() => this.props.select(`/footer/links/${link.id}`)}>
											Edit
										</button>
									</div>
								</SortableItem>
							))}
						</Sortable>
					</div>
				)}

				<div class={s.buttonRow} style={{ marginTop: '16px' }}>
					<Button dark primary icon={'plus'} onClick={() => addFooterLinkInColumn(this.footerPath, 2)} disabled={readonly}>
						Add link
					</Button>
					{column2CustomLinks.length > 0 && (
						<Button dark text danger onClick={() => this.resetFooterLinks(2)} disabled={readonly}>
							Reset links
						</Button>
					)}
				</div>
			</div>
		);
	}

	renderSettings() {
		let content;
		let nav = [{ label: 'Footer', path: '/footer' }];

		const activeElement = this.props.activeElement;

		if (activeElement === '/footer') {
			content = this.renderFocusFooterEditor();
		}

		if (activeElement.startsWith('/footer/links/')) {
			const linkNumber = activeElement.split('/').pop();
			content = this.renderFooterLinkSettings(linkNumber);
			nav = [
				{ label: 'Footer', path: '/footer' },
				{ label: 'Link', path: `/footer/links/${linkNumber}` },
			];
		}

		if (activeElement.startsWith('/footer/socials')) {
			// if activeElement has another / after socials, go to that social setting
			if (activeElement.split('/').length > 3) {
				const socialIndex = activeElement.split('/').pop();
				content = this.renderSocialSettings(socialIndex);

				// For Focus Features, get the actual social name from navigation data
				const navigationData = Store.get().data?.navigation?.content;
				const globalSocials = navigationData?.footer?.social || [];
				const social = globalSocials[parseInt(socialIndex)];
				const socialLabel = social?.name || 'Social';

				nav = [
					{ label: 'Social media', path: `/footer/socials` },
					{ label: socialLabel, path: activeElement },
				];
			} else {
				content = this.renderSocialsSettings();
				nav = [{ label: 'Social media', path: `/footer/socials` }];
			}

			return (
				<div class={s.settings}>
					{this.renderSettingsNavigation(nav)}
					{content}
				</div>
			);
		}

		return (
			<div class={s.settings}>
				{this.renderSettingsNavigation(nav)}
				{content}
			</div>
		);
	}
}